// ================== Converts form state to API payload for InsertOnlineAppointmentRegistrationData ==================
export async function mapFormToPayload(
  form,
  helpers,
  hospitalName,
  totalAmount
) {
  // Helper: Convert image or PDF URL to Base64
  async function convertUrlToBase64(url) {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(",")[1]); // Remove "data:*/*;base64,"
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Failed to convert URL to base64:", error);
      return "";
    }
  }

  // Helper: Calculate age from DOB
  const getAgeParts = (dob) => {
    if (!dob) return { years: "0", months: "0", days: "0" };
    const [yyyy, mm, dd] = dob.split("-").map(Number);
    const birthDate = new Date(yyyy, mm - 1, dd);
    const today = new Date();
    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    let days = today.getDate() - birthDate.getDate();

    if (days < 0) {
      months--;
      days += new Date(today.getFullYear(), today.getMonth(), 0).getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    return {
      years: years.toString(),
      months: months.toString(),
      days: days.toString(),
    };
  };

  // Helper: Format date to DD-MM-YYYY
  const formatDateDMY = (dateStr) => {
    if (!dateStr) return "";
    const [yyyy, mm, dd] = dateStr.split("-");
    return `${dd}-${mm}-${yyyy}`;
  };

  // Helper: Format date to YYYY-MM-DD HH:mm
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    return `${dateStr}`;
  };

  // Doctor code logic
  let doctorCode = sessionStorage.getItem("selectedDoctorCode") || "";
  if (!doctorCode && form.doctor && Array.isArray(helpers?.Doctor)) {
    const found = helpers.Doctor.find(
      (d) => d.RECORD_NAME === form.doctor || d.RECORD_ID === form.doctor
    );
    if (found) doctorCode = found.RECORD_ID;
  }

  const age = getAgeParts(form.dob);
  const [rel1 = {}, rel2 = {}] = form.relatives || [];

  // ================== Document Mapping ==================
  const doc_dt_list = [];

  // Aadhaar (Indian)
  if (
    form.nationality?.toUpperCase() === "R_1_2" &&
    form.document?.documentNumber
  ) {
    let base64 = form.document?.aadharFile?.base64 || "";

    // Fallback: Convert from preview URL if base64 not found
    if (!base64 && form.document?.aadharpreviewUrl) {
      base64 = await convertUrlToBase64(form.document.aadharpreviewUrl);
    }

    doc_dt_list.push({
      documenT_TYPE: "R_11_1", // Aadhaar
      doC_NO: form.document.documentNumber,
      issuE_DATE: "",
      enD_DATE: "",
      expirY_DATE: "",
      filE_NAME: form.document.aadharFile?.name || "",
      base64string: base64,
    });
  }
  // Passport & Medical Visa (Non-Indian)
  else if (form.nationality !== "R_1_2") {
    const types = [
      { type: "passport", code: "R_11_4" },
      { type: "medicalvisa", code: "R_11_7" },
    ];

    for (const { type, code } of types) {
      const docNo = form.document?.[`${type}No`];
      if (docNo) {
        let base64 = form.document?.[`${type}File`]?.base64 || "";

        // Fallback: Convert from preview URL if base64 not found
        if (!base64 && form.document?.[`${type}previewUrl`]) {
          base64 = await convertUrlToBase64(form.document[`${type}previewUrl`]);
        }

        doc_dt_list.push({
          documenT_TYPE: code,
          doC_NO: docNo,
          issuE_DATE: form.document?.[`${type}IssueDate`] || "",
          enD_DATE: form.document?.[`${type}EndDate`] || "",
          expirY_DATE: form.document?.[`${type}ExpDate`] || "",
          filE_NAME: form.document?.[`${type}File`]?.name || "",
          base64string: base64,
        });
      }
    }
  }

  // ================== Payment Details ==================
  const dtPaymentDetails_list = [
    {
      paymode: "Cash",
      otheR_DETAILS: "",
      amount: totalAmount,
    },
  ];

  // ================== Assign 'type' using context value ==================
  const type = sessionStorage.getItem("selectedHospital");

  // ================== Final Payload ==================
  return {
    uhiD_ID: form.uhid || "",
    namE_AB: form.salutation || "",
    pT_NAME: form.name || "",
    contact: form.phone || "",
    religion: form.religion || "",
    caste: form.caste || "",
    gender: form.gender || "",
    dob: form.dob ? formatDateDMY(form.dob) : "",
    agE_YY: age.years,
    agE_MM: age.months,
    agE_DD: age.days,
    nationality: form.nationality || "",
    pT_IMAGE: "test.jpg", // hardcoded for now
    emaiL_ID: form.email || "",
    occupation: form.occupation || "",
    presenT_ADDRESS: form.presentAddress?.address || "",
    presenT_PS: form.presentAddress?.policeStation || "",
    presenT_DISTRICT_CITY: form.presentAddress?.cityDistrict || "",
    presenT_COUNTRY: form.presentAddress?.country || "",
    presenT_STATE: form.presentAddress?.state || "",
    presenT_PINCODE: form.presentAddress?.pinCode || "",
    permA_ADDRESS: form.permanentAddress?.address || "",
    permA_PS: form.permanentAddress?.policeStation || "",
    permA_DISTRICT_CITY: form.permanentAddress?.cityDistrict || "",
    permA_COUNTRY: form.permanentAddress?.country || "",
    permA_STATE: form.permanentAddress?.state || "",
    permA_PINCODE: form.permanentAddress?.pinCode || "",
    reL1_AB: rel1.salutation || "",
    reL1_NAME: rel1.name || "",
    reL1_RELATION: rel1.relation || "",
    reL1_CONTACT: rel1.phone || "",
    reL2_AB: rel2.salutation || "",
    reL2_NAME: rel2.name || "",
    reL2_RELATION: rel2.relation || "",
    reL2_CONTACT: rel2.phone || "",
    admissioN_DEPARTMENT: form.department || "",
    registratioN_AMT: "0",
    doctoR_CODE: doctorCode,
    conS_CHARGE: "0.0",
    doc_dt_list,
    dtPaymentDetails_list,
    monthlY_INCOME: form.monthlyIncome || "00",
    type,
    doa: `${form.bookingDate} ${
      sessionStorage.getItem("selectedDoctorFromTime") || ""
    }`,
    file_Base64String: "", // Reserved for main file if needed later
  };
}
